import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PaymentProcessing.css';

const API = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

const defaultFormValues = {
  name: '',
  processor: 'stripe',
  publishable_key: '',
  secret_key: '',
  webhook_secret: '',
  success_url: '',
  cancel_url: '',
  statement_descriptor: '',
};

const createDefaultForm = () => ({ ...defaultFormValues });

export default function PaymentProcessing() {
  const [processors, setProcessors] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(createDefaultForm());
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const selectedIsNew = selectedId === 'new';
  const handleClose = () => navigate('/shipping-funding');

  const fetchProcessors = async () => {
    try {
      setLoadingList(true);
      const res = await fetch(`${API}/api/appconfig/payment-processors`, { credentials: 'include' });
      const data = await res.json().catch(() => []);
      if (Array.isArray(data)) {
        setProcessors(data);
        if (data.length === 0) {
          setSelectedId('new');
          setForm(createDefaultForm());
        } else if (!selectedId || selectedId === 'new' || !data.some((p) => String(p.id) === String(selectedId))) {
          setSelectedId(String(data[0].id));
        }
      } else {
        setProcessors([]);
        setSelectedId('new');
        setForm(createDefaultForm());
      }
    } catch {
      setProcessors([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchProcessorDetail = async (id) => {
    if (!id || id === 'new') {
      setForm(createDefaultForm());
      return;
    }
    try {
      setLoadingDetail(true);
      const res = await fetch(`${API}/api/appconfig/payment-processors/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load processor');
      const data = await res.json();
      setForm({
        name: data.name || '',
        processor: data.processor || 'stripe',
        publishable_key: data.publishable_key || '',
        secret_key: data.secret_key || '',
        webhook_secret: data.webhook_secret || '',
        success_url: data.success_url || '',
        cancel_url: data.cancel_url || '',
        statement_descriptor: data.statement_descriptor || '',
      });
    } catch (err) {
      alert(err.message || 'Failed to load processor');
      setForm(createDefaultForm());
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => { fetchProcessors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedId && selectedId !== 'new') {
      fetchProcessorDetail(selectedId);
    } else if (selectedId === 'new') {
      setForm(createDefaultForm());
    }
  }, [selectedId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.name.trim()) {
        alert('Name is required');
        setSaving(false);
        return;
      }
      const method = selectedIsNew || !selectedId ? 'POST' : 'PUT';
      const url = selectedIsNew || !selectedId
        ? `${API}/api/appconfig/payment-processors`
        : `${API}/api/appconfig/payment-processors/${selectedId}`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save processor');
      }
      await fetchProcessors();
      if (method === 'POST') {
        const created = await res.json().catch(() => null);
        if (created?.id) setSelectedId(String(created.id));
      } else {
        await fetchProcessorDetail(selectedId);
      }
      alert('Saved');
    } catch (e) {
      alert(e.message || 'Failed to save processor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || selectedIsNew) {
      setSelectedId('');
      setForm(createDefaultForm());
      return;
    }
    if (!window.confirm('Delete this payment processor? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/appconfig/payment-processors/${selectedId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Failed to delete processor');
      }
      setSelectedId('');
      setForm(createDefaultForm());
      await fetchProcessors();
    } catch (e) {
      alert(e.message || 'Failed to delete processor');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="payment-processing-overlay" role="dialog" aria-modal="true" aria-labelledby="payment-processing-title">
      <div className="payment-processing-modal">
        <div className="payment-processing-header">
          <h2 id="payment-processing-title">Payment Processing</h2>
          <button className="eml-modal__close" onClick={handleClose} aria-label="Close">✕</button>
        </div>
        <div className="payment-processing-body">
          <div className="field-row">
            <label>
              <span>Select Processor</span>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={loadingList}
              >
                {processors.map((proc) => (
                  <option key={proc.id} value={proc.id}>{proc.name} ({proc.processor})</option>
                ))}
                <option value="new">+ Add New Processor</option>
              </select>
            </label>
            <button className="btn btn-secondary" onClick={() => setSelectedId('new')}>
              {selectedIsNew ? 'Creating New' : 'Add New'}
            </button>
          </div>

          <div className="form-grid">
            <label>
              <span>Display Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Stripe (Live)"
              />
            </label>
            <label>
              <span>Processor</span>
              <select value={form.processor} onChange={(e) => handleChange('processor', e.target.value)}>
                <option value="stripe">Stripe</option>
              </select>
            </label>
            <label className="full">
              <span>Publishable Key</span>
              <input
                type="text"
                value={form.publishable_key}
                onChange={(e) => handleChange('publishable_key', e.target.value)}
              />
            </label>
            <label className="full">
              <span>Secret Key</span>
              <input
                type="text"
                value={form.secret_key}
                onChange={(e) => handleChange('secret_key', e.target.value)}
              />
            </label>
            <label className="full">
              <span>Webhook Secret</span>
              <input
                type="text"
                value={form.webhook_secret}
                onChange={(e) => handleChange('webhook_secret', e.target.value)}
              />
            </label>
            <label className="full">
              <span>Success URL</span>
              <input
                type="text"
                value={form.success_url}
                onChange={(e) => handleChange('success_url', e.target.value)}
                placeholder="https://example.com/shipping-funding?stripe=success"
              />
            </label>
            <label className="full">
              <span>Cancel URL</span>
              <input
                type="text"
                value={form.cancel_url}
                onChange={(e) => handleChange('cancel_url', e.target.value)}
                placeholder="https://example.com/shipping-funding?stripe=cancel"
              />
            </label>
            <label className="full">
              <span>Statement Descriptor</span>
              <input
                type="text"
                value={form.statement_descriptor}
                onChange={(e) => handleChange('statement_descriptor', e.target.value)}
                placeholder="Easy3PL Shipping Funding"
              />
            </label>
          </div>
        </div>
        <div className="payment-processing-footer">
          <button className="btn" onClick={handleSave} disabled={saving || loadingDetail}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-secondary" onClick={handleDelete} disabled={deleting || (!selectedId && !selectedIsNew)}>
            {selectedIsNew ? 'Cancel New' : (deleting ? 'Deleting…' : 'Delete')}
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
